/**
 * Hook React Query para el listado del Action Center unificado (0666b).
 *
 * Consume `GET /action-center/all` (creado en 0666a). Usa la cache
 * global de React Query: invalidación tras mutate (aprobar/rechazar) provoca
 * refetch automático.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { request } from '../../../shared/services/api';

export type ActionItemTipo =
  | 'ORGANIGRAMA'
  | 'DOC'
  | 'FLUJOGRAMA'
  | 'APROBACION_DOCUMENTO'
  | 'APROBACION_PETICION'
  | 'APROBACION_COMPRA';

export interface ActionItem {
  id: string;
  tipo: ActionItemTipo;
  source: 'workflow' | 'legacy_sign';
  codigo: string | null;
  titulo: string;
  asunto: string | null;
  alcance: string | null;
  solicitante: { id: string | null; nombre: string | null };
  fechaSolicitud: string;
  deadline: string | null;
  estado: string;
  urlAccion: string | null;
}

export interface ActionCenterListResult {
  data: ActionItem[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    counts: Record<ActionItemTipo, number>;
  };
}

/** Vista temporal del Action Center (0669). */
export type VistaActionCenter = 'PENDIENTES' | 'EN_CURSO' | 'HISTORICO';

export interface ActionCenterListFilters {
  vista?: VistaActionCenter;
  q?: string;
  tipo?: ActionItemTipo | 'all';
  estado?: string;
  page?: number;
  pageSize?: number;
}

function buildQuery(filters: ActionCenterListFilters): string {
  const params = new URLSearchParams();
  if (filters.vista && filters.vista !== 'PENDIENTES') params.set('vista', filters.vista);
  if (filters.q && filters.q.trim().length > 0) params.set('q', filters.q.trim());
  if (filters.tipo && filters.tipo !== 'all') params.set('tipo', filters.tipo);
  if (filters.estado) params.set('estado', filters.estado);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export function useActionCenterList(
  filters: ActionCenterListFilters,
): UseQueryResult<ActionCenterListResult, Error> {
  return useQuery({
    queryKey: ['actionCenter', 'list', filters] as const,
    queryFn: async () => {
      const qs = buildQuery(filters);
      return request<ActionCenterListResult>(`/action-center/all${qs}`);
    },
  });
}

/**
 * Hook complementario para el badge global. Usa key estable separada del
 * listado para no recachear cuando el usuario cambia filtros.
 */
export function useActionCenterCount(): UseQueryResult<{ data: { count: number } }, Error> {
  return useQuery({
    queryKey: ['actionCenter', 'count'] as const,
    queryFn: () => request<{ data: { count: number } }>('/action-center/count'),
  });
}
